import React, { useState } from 'react';
import './API.css';

const API: React.FC = () => {
  const [activeEndpoint, setActiveEndpoint] = useState<string | null>(null);

  const endpoints = [
    {
      id: 'watchlist',
      title: 'Watchlist Management',
      description: 'Add, retrieve, and manage items on watchlists to track high-risk entities.',
      methods: [
        { method: 'POST', path: '/v1/lists/watchlist', description: 'Add items to watchlist' },
        { method: 'GET', path: '/v1/lists/watchlist', description: 'Get watchlist contents' },
        { method: 'POST', path: '/v1/lists/watchlist/query', description: 'Check if items are on watchlist' },
        { method: 'POST', path: '/v1/lists/watchlist/delete', description: 'Remove items from watchlist' }
      ]
    },
    {
      id: 'trustedlist',
      title: 'Trusted Lists',
      description: 'Manage trusted entities for streamlined operations.',
      methods: [
        { method: 'POST', path: '/v1/lists/trustedlist', description: 'Add items to trusted list' },
        { method: 'GET', path: '/v1/lists/trustedlist', description: 'Get trusted list contents' },
        { method: 'POST', path: '/v1/lists/trustedlist/query', description: 'Check if items are on trusted list' },
        { method: 'POST', path: '/v1/lists/trustedlist/delete', description: 'Remove items from trusted list' }
      ]
    },
    {
      id: 'profiles',
      title: 'Company Profiles',
      description: 'Retrieve detailed company information including risk scores and financial data.',
      methods: [
        { method: 'POST', path: '/v1/companyprofiles', description: 'Get comprehensive company profile data' }
      ]
    },
    {
      id: 'relationships',
      title: 'Company Relationships',
      description: 'Query relationships between companies for advanced risk analysis.',
      methods: [
        { method: 'POST', path: '/v1/companyrelationship', description: 'Analyze relationships between companies' }
      ]
    }
  ];

  return (
    <div className="api-page">
      <div className="api-hero">
        <div className="api-hero-content">
          <h1>Mycelo Integration API</h1>
          <p className="api-hero-subtitle">Version 1.1.0</p>
          <p className="api-hero-description">
            Integrate advanced credit scoring and risk analysis into your applications with our comprehensive REST API.
          </p>
          <div className="api-hero-badges">
            <span className="api-badge">OAuth2 Secured</span>
            <span className="api-badge">REST API</span>
            <span className="api-badge">Real-time Data</span>
          </div>
        </div>
      </div>

      <div className="api-content">
        <section className="api-section">
          <h2 className="api-section-title">Base URLs</h2>
          <div className="endpoints-grid">
            <div className="endpoint-card">
              <h3>Integration Environment</h3>
              <div className="endpoint-url">
                <span className="url-label">API:</span>
                <code>https://api.int.breadify.me</code>
              </div>
              <div className="endpoint-url">
                <span className="url-label">Auth:</span>
                <code>https://auth.int.breadify.me</code>
              </div>
            </div>
            <div className="endpoint-card">
              <h3>Production Environment</h3>
              <div className="endpoint-url">
                <span className="url-label">API:</span>
                <code>https://api.breadify.me</code>
              </div>
              <div className="endpoint-url">
                <span className="url-label">Auth:</span>
                <code>https://auth.breadify.me</code>
              </div>
            </div>
          </div>
        </section>

        <section className="api-section">
          <h2 className="api-section-title">Authentication</h2>
          <div className="auth-container">
            <div className="auth-explanation">
              <p>All operations require an OAuth2 access token. Use your <code>client_id</code> and <code>client_secret</code> to obtain an access token.</p>
            </div>
            <div className="code-example">
              <h4>Get Access Token</h4>
              <pre className="code-block">
                <code>{`curl -X POST -u "<your_client_id>:<your_client_secret>" \\
  -d "grant_type=client_credentials" \\
  https://auth.int.breadify.me/token`}</code>
              </pre>
            </div>
            <div className="code-example">
              <h4>Use Token in Requests</h4>
              <pre className="code-block">
                <code>{`--header "Authorization: Bearer <access_token>"`}</code>
              </pre>
            </div>
          </div>
        </section>

        <section className="api-section">
          <h2 className="api-section-title">API Endpoints</h2>
          <div className="endpoints-container">
            {endpoints.map((endpoint) => (
              <div 
                key={endpoint.id} 
                className={`endpoint-group ${activeEndpoint === endpoint.id ? 'active' : ''}`}
                onClick={() => setActiveEndpoint(activeEndpoint === endpoint.id ? null : endpoint.id)}
              >
                <div className="endpoint-header">
                  <h3>{endpoint.title}</h3>
                  <p>{endpoint.description}</p>
                  <span className="endpoint-toggle">
                    {activeEndpoint === endpoint.id ? '−' : '+'}
                  </span>
                </div>
                {activeEndpoint === endpoint.id && (
                  <div className="endpoint-methods">
                    {endpoint.methods.map((method, index) => (
                      <div key={index} className="method-item">
                        <span className={`method-badge ${method.method.toLowerCase()}`}>
                          {method.method}
                        </span>
                        <div className="method-details">
                          <code className="method-path">{method.path}</code>
                          <p className="method-description">{method.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="api-section">
          <h2 className="api-section-title">Example Request</h2>
          <div className="example-container">
            <div className="example-description">
              <h4>Retrieve Company Profile</h4>
              <p>Get comprehensive company information including risk scores, financial data, and list statuses.</p>
            </div>
            <div className="code-example">
              <pre className="code-block">
                <code>{`POST /v1/companyprofiles
Content-Type: application/json
Authorization: Bearer <access_token>

{
  "orgNumbers": [
    "5593957763",
    "5521835633"
  ]
}`}</code>
              </pre>
            </div>
          </div>
        </section>

        <section className="api-section">
          <h2 className="api-section-title">Response Format</h2>
          <div className="response-container">
            <p>All responses are returned in JSON format with comprehensive error handling and status codes.</p>
            <div className="status-codes">
              <div className="status-code success">
                <span className="code">200</span>
                <span className="description">Success</span>
              </div>
              <div className="status-code error">
                <span className="code">400</span>
                <span className="description">Bad Request</span>
              </div>
              <div className="status-code error">
                <span className="code">401</span>
                <span className="description">Unauthorized</span>
              </div>
            </div>
          </div>
        </section>

        <section className="api-section">
          <h2 className="api-section-title">Getting Started</h2>
          <div className="getting-started">
            <div className="step-cards">
              <div className="step-card">
                <div className="step-number">1</div>
                <h4>Get API Credentials</h4>
                <p>Contact Mycelo to receive your client_id and client_secret for API access.</p>
              </div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h4>Obtain Access Token</h4>
                <p>Use your credentials to get an OAuth2 access token from the authentication endpoint.</p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h4>Make API Calls</h4>
                <p>Include the Bearer token in your API requests to access company data and manage lists.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default API;